import { useStore as useZustandStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
const SLOT_IDS = Object.freeze(['slot-0', 'slot-1', 'slot-2', 'slot-3']);
const DEFAULT_KINDS = Object.freeze(['rect', 'smith', 'vswr', 'phase']);
const PRESET_STORAGE_KEY = 'nanovnaweb.chart.preset';
const VALID_PRESETS = [
    'single',
    'horizontal-pair',
    'vertical-pair',
    'quad',
];
function readStoredPreset() {
    try {
        const raw = globalThis.localStorage?.getItem(PRESET_STORAGE_KEY);
        if (raw !== null && raw !== undefined && VALID_PRESETS.includes(raw)) {
            return raw;
        }
    }
    catch {
        // localStorage unavailable (SSR, private browsing, Node tests).
    }
    return 'quad';
}
function writeStoredPreset(p) {
    try {
        globalThis.localStorage?.setItem(PRESET_STORAGE_KEY, p);
    }
    catch {
        // ignore
    }
}
function defaultSlot(id, index) {
    const kind = DEFAULT_KINDS[index] ?? 'rect';
    return {
        id,
        kind,
        primarySParam: 's11',
        primaryColor: `var(--color-trace-${(index % 8) + 1})`,
        overlayIds: Object.freeze([]),
        crosshairEnabled: true,
    };
}
export function createChartStore() {
    const initialSlots = {};
    for (const [i, id] of SLOT_IDS.entries()) {
        initialSlots[id] = defaultSlot(id, i);
    }
    const store = createStore()(() => ({
        preset: readStoredPreset(),
        slots: Object.freeze({ ...initialSlots }),
        slotOrder: Object.freeze([...SLOT_IDS]),
        selectedSlotId: 'slot-0',
        hoverFrequencyHz: null,
        hoverActiveTraceId: null,
    }));
    const actions = {
        setPreset(p) {
            store.setState({ preset: p });
            writeStoredPreset(p);
        },
        setSlot(id, patch) {
            const cur = store.getState().slots[id];
            if (cur === undefined)
                return;
            const nextSlot = {
                ...cur,
                ...patch,
                overlayIds: patch.overlayIds !== undefined ? Object.freeze([...patch.overlayIds]) : cur.overlayIds,
            };
            store.setState({
                slots: Object.freeze({ ...store.getState().slots, [id]: nextSlot }),
            });
        },
        selectSlot(id) {
            store.setState({ selectedSlotId: id });
        },
        toggleOverlay(slotId, recordingId) {
            const cur = store.getState().slots[slotId];
            if (cur === undefined)
                return;
            const has = cur.overlayIds.includes(recordingId);
            const nextIds = has
                ? cur.overlayIds.filter((x) => x !== recordingId)
                : [...cur.overlayIds, recordingId];
            actions.setSlot(slotId, { overlayIds: Object.freeze([...nextIds]) });
        },
        toggleCrosshair(slotId) {
            const cur = store.getState().slots[slotId];
            if (cur === undefined)
                return;
            actions.setSlot(slotId, { crosshairEnabled: !cur.crosshairEnabled });
        },
        setHoverFrequency(hz) {
            store.setState({ hoverFrequencyHz: hz, hoverActiveTraceId: null });
        },
        setHover(hz, traceId) {
            store.setState({ hoverFrequencyHz: hz, hoverActiveTraceId: traceId });
        },
    };
    return { store, actions };
}
export function useChartStore(store, selector) {
    return useZustandStore(store, selector);
}
//# sourceMappingURL=chart-store.js.map